import type { Way } from '@/types/common.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';
import { roundToFixed } from '@/utils/number.ts';

import type { selectSwapPrices } from './rfs.ts';

/**
 * Price fields computed from `selectSwapPrices` that are sent in the swap RFS execution payload.
 *
 * This type is the single source of truth for which values are sent and how they are computed.
 * It is consumed by:
 *   - `SwapRates.tsx` — production: spreads the result into the `executeSwapRfs` call
 *   - `rfs.test.ts`  — tests: compares the result against values captured from FX
 */
export type SwapExecutionPrices = {
  /**
   * Client near all-in price (= spot + nearDeltaRate), rounded to `precision + 3` decimals.
   *
   * Matches FX behavior: `roundToPrecision(spot_p2 + nearDelta_p2, p+3)` where both operands
   * are individually rounded to p+2 first. When near price is pinned by the user, the
   * pinned value is returned as-is (FX behavior for manual near price).
   */
  nearPrice: number;
  /**
   * Client far all-in price (= spot + farDeltaRate with margin), rounded to `precision + 3` decimals.
   *
   * Matches FX behavior: `roundToPrecision(spot_p2 + farDelta_p2, p+3)` where both operands
   * are individually rounded to p+2 first. For historical (off-market) swaps where far price
   * is user-pinned, the pinned value is returned as-is (FX behavior).
   */
  farPrice: number;
  /**
   * Client near delta rate, rounded to `precision + 2` decimals.
   *
   * Matches FX: `roundToPrecision(nearRate, p+2)` (= `getDisplayNearPoints.raw`).
   * Computed as `convertPointsToRate(prices.nearPoints[side], p+2, p-1)`.
   * Note: `prices.nearPoints[side]` = `nearDeltaRate × 10^(p-1)` with NO prior rounding —
   * it is a raw float multiplication in `convertRateToPoints`. The p+2 round applied here
   * is the first and only rounding on this value.
   * There is no `nearPointsWithoutMargin` because the commercial swap margin applies only
   * to the far leg — the near delta rate from the quote is already the client value.
   */
  nearPoints: number;
  /**
   * Client far delta rate after commercial margin, rounded to `precision + 2` decimals.
   *
   * Matches FX: `roundToPrecision(farDelta, p+2)` (= `getDisplayFarPoints.raw`).
   * Computed as `convertPointsToRate(prices.farPoints[side], p+2, p-1)`.
   * Note: `prices.farPoints[side]` = `farDeltaRate × 10^(p-1)` with NO prior rounding —
   * it is a raw float multiplication in `convertRateToPoints`. The p+2 round applied here
   * is the first and only rounding on this value.
   */
  farPointsWithMargin: number;
  /**
   * Trader far delta rate before commercial margin, in rate units.
   * Taken directly from the quote (no transformation, no rounding).
   */
  farPointsWithoutMargin: number;
  /**
   * Sales near all-in rate, in rate units. Taken directly from the quote.
   * Defaults to 0 when null.
   */
  nearSalesAllInRate: number;
  /**
   * Sales far all-in rate, in rate units. Taken directly from the quote.
   * Defaults to 0 when null.
   */
  farSalesAllInRate: number;
};

type SwapPrices = NonNullable<ReturnType<typeof selectSwapPrices>>;

/**
 * Computes the price fields sent in the swap RFS execution payload.
 *
 * This is the single source of truth for all transformations and roundings applied to prices
 * before they reach the backend. It is called in two places:
 *
 *   1. `SwapRates.tsx` — `executeBid` / `executeAsk`: spreads the result directly into the
 *      `executeSwapRfs` mutation call that sends the HTTP request.
 *
 *   2. `rfs.test.ts` — unit tests: calls this function with a Redux state reconstructed from
 *      a real FX execution trace, then compares the result against the values FX actually sent.
 *
 * Because both production and tests call the SAME function, any change to a rounding rule or
 * transformation here will break the tests — which is exactly the intended safety net.
 *
 * @param prices - Output of `selectSwapPrices` for the tile being executed
 * @param way - Execution way ('Bid' or 'Ask')
 */
export function buildSwapExecutionPrices(prices: SwapPrices, way: Way): SwapExecutionPrices {
  const p = prices.currencyPairPrecision;
  const side = way === 'Bid' ? 'bid' : 'ask';

  // Spot rounded to p+2, matching FX's getDisplaySpot (roundToSwapPrecision(precision) = p+2).
  const roundedSpot = roundToFixed(prices.spot[side], p + 2);

  // Far delta rate rounded to p+2, matching FX's getDisplayFarPoints.raw (roundToSwapPrecision(precision) = p+2).
  // prices.farPoints[side] = farDeltaRate × 10^(p-1) with NO prior rounding — raw multiplication only.
  const roundedFarDelta = convertPointsToRate(prices.farPoints[side], p + 2, p - 1);

  // Near delta rate rounded to p+2 — same pattern as farDelta.
  // prices.nearPoints[side] = nearDeltaRate × 10^(p-1) with NO prior rounding.
  const roundedNearDelta = convertPointsToRate(prices.nearPoints[side], p + 2, p - 1);

  // nearPrice: when near price is pinned by the user, return it as-is (FX behavior).
  // Otherwise: replicate FX's two-step rounding — round components to p+2 first, then sum to p+3.
  // FX: roundToPrecision(spot_p2 + nearDelta_p2, p+3)
  const nearPrice =
    prices.pinnedNearPrice !== undefined ? prices.nearPrice[side] : roundToFixed(roundedSpot + roundedNearDelta, p + 3);

  // farPrice: when far price is pinned (historical swap), return it as-is (FX behavior).
  // Otherwise: replicate FX's two-step rounding — round components to p+2 first, then sum to p+3.
  // FX: roundToPrecision(spot_p2 + farDelta_p2, p+3)
  const farPrice = prices.isPinnedFarPrice ? prices.farPrice[side] : roundToFixed(roundedSpot + roundedFarDelta, p + 3);

  return {
    nearPrice,
    farPrice,
    nearPoints: roundedNearDelta,
    farPointsWithMargin: roundedFarDelta,
    farPointsWithoutMargin: prices.rfsPrice.quote.farPoints[side],
    nearSalesAllInRate: prices.rfsPrice.quote.nearSalesAllInRate?.[side] ?? 0,
    farSalesAllInRate: prices.rfsPrice.quote.farSalesAllInRate?.[side] ?? 0,
  };
}
